import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import EvaluationCard from "@/components/EvaluationCard";
import { ClipboardList, Calendar, QrCode, Zap } from "lucide-react";

export default function EvaluatorDashboard() {
  const { user } = useAuth();
  const [recent, setRecent] = useState([]);
  const [camps, setCamps] = useState([]);

  useEffect(() => {
    api.get("/evaluations", { params: { evaluator_user_id: user.id } }).then(({ data }) => setRecent(data.items || []));
    api.get("/camps").then(({ data }) => setCamps(data.items || []));
  }, [user.id]);

  const submitted = recent.filter((r) => r.status === "submitted").length;
  const drafts = recent.filter((r) => r.status === "draft").length;

  return (
    <PublicLayout>
      <section className="bg-[#0A192F] text-white py-12 border-b-4 border-[#E63946]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="label-overline text-[#E63946]">Evaluator Console</div>
          <h1 data-testid="evaluator-dashboard-title" className="font-display text-4xl uppercase tracking-tighter mt-2">
            {user.name}
          </h1>
          <p className="text-sm text-white/70 mt-2">Submit verified 20-80 scouting reports. Your evaluations feed directly into CBR Overall Ratings.</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/evaluator/dashboard"
              data-testid="camp-dashboard-cta"
              className="inline-flex items-center gap-2 bg-white text-[#0A192F] px-6 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm hover:bg-white/90 transition-colors"
            >
              <ClipboardList className="w-4 h-4" /> Camp Dashboard
            </Link>
            <Link
              to="/quick-eval"
              data-testid="quick-eval-cta"
              className="inline-flex items-center gap-2 bg-[#E63946] hover:bg-[#D90429] text-white px-6 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm transition-colors"
            >
              <Zap className="w-4 h-4" /> Quick Eval Mode
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-[#F8F9FA] py-10">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 grid md:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <div className="flex items-center justify-between"><div className="label-overline">Submitted</div><ClipboardList className="w-5 h-5 text-[#E63946]" /></div>
            <div className="font-data text-4xl mt-2">{submitted}</div>
          </div>
          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <div className="flex items-center justify-between"><div className="label-overline">Drafts</div><ClipboardList className="w-5 h-5 text-amber-600" /></div>
            <div className="font-data text-4xl mt-2">{drafts}</div>
          </div>
          <div className="bg-[#E63946] text-white p-5 rounded-sm">
            <div className="flex items-center justify-between"><div className="label-overline text-white/70">Active Camps</div><Calendar className="w-5 h-5" /></div>
            <div className="font-data text-4xl mt-2">{camps.filter((c) => c.status === "open").length}</div>
          </div>
        </div>
      </section>

      <section className="bg-white py-10">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <h2 className="font-display text-2xl uppercase tracking-tight">How to Submit an Evaluation</h2>
          <ol className="mt-3 text-sm text-slate-700 space-y-2 list-decimal list-inside">
            <li>Scan a player&apos;s QR credential card with your phone camera, OR open <code className="bg-slate-100 px-1.5 py-0.5 rounded-sm">/qr/&lt;token&gt;</code> manually.</li>
            <li>You&apos;ll land on the player&apos;s admin profile with a one-click <strong>New Evaluation</strong> button.</li>
            <li>Pick the camp context, grade tools on the 20-80 scale, set OFV, add notes, then submit.</li>
          </ol>
        </div>
      </section>

      <section className="bg-[#F8F9FA] py-10">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <h2 className="font-display text-2xl uppercase tracking-tight mb-4">Your Recent Reports</h2>
          {recent.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">
              <QrCode className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              No evaluations yet. Scan a player&apos;s QR code at a CBR camp to start your first report.
            </div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-4">
              {recent.map((e) => (
                <div key={e.id} className="relative">
                  <Link to={`/prospects/${e.player_id}`} className="absolute inset-0 z-10" aria-label="View player profile" />
                  <EvaluationCard evaluation={e} hideEvaluator />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
